import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { attendanceConceptService } from '@/api/domains/attendance';
import { useExport } from '@/hooks/ui/useExport';
import { useUI } from '@/hooks/ui/useUI';
import type { UIExportFormat } from '@/types/fileExport.types';


export interface UseAttendanceConceptExportParams {
  searchText?: string;
}

export interface UseAttendanceConceptExportResult {
  onExport: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

export function useAttendanceConceptExport(
  params: UseAttendanceConceptExportParams = {},
): UseAttendanceConceptExportResult {
  const { t: translate } = useTranslation();
  const { showNotification } = useUI();

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => attendanceConceptService.export({ searchText: params.searchText }),
    successMessage: translate('attendance:attendanceConcept.messages.exportSuccess'),
    errorMessage: translate('attendance:attendanceConcept.messages.exportError'),
  });

  const onExport = useCallback(async (format: UIExportFormat) => {
    if (format !== 'EXCEL') {
      showNotification(
        'error',
        translate('attendance:attendanceConcept.messages.exportError'),
      );
      return;
    }
    await exportFile();
  }, [exportFile, showNotification, translate]);

  return { onExport, isExporting };
}

export default useAttendanceConceptExport;
